import * as Actions from '../actions/timeOffActions';
import TimeOff from '../../entities/timeOff';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type TimeOffAction = { 
    type: string; 
    timeOff: TimeOff | null; 
    timeOffId: string | TimeOff; 
    partial: boolean; 
    lastFetch: number; 
    timeOffs: TimeOff[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<TimeOff> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {TimeOff[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<TimeOff> | null, firmId: string, userId: string, updates?: TimeOff[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'timeOff', 'timeOffs', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<TimeOff>} data
 * @param {TimeOff[]} [updates]
 * @returns {ArrayMap<TimeOff>}
 */
function updateStorage(data: ArrayMap<TimeOff>, updates?: TimeOff[]): ArrayMap<TimeOff> {
	if (logging) console.debug('⮂ timeOffReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {TimeOff[]} [updates]
 * @returns {ArrayMap<TimeOff>}
 */
function updateStorageNoReturn(updates?: TimeOff[]): void {
	if (logging) console.debug('⮂ timeOffReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {TimeOff[]} [data]
 * @returns {ArrayMap<TimeOff>}
 */
function createState(data?: TimeOff[] | ArrayMap<TimeOff>): ArrayMap<TimeOff> {
	if (!data) return ArrayMapRegistry.clone<TimeOff>('timeOff', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<TimeOff>('timeOff', data.map(e => e));
    else return ArrayMapRegistry.clone<TimeOff>('timeOff', data);
}

function timeOffs(state: ArrayMap<TimeOff>, action: TimeOffAction): ArrayMap<TimeOff> {
	if (!state) state = createState();

	let st: ArrayMap<TimeOff>;

	switch (action.type) {
		// action.timeOff - the new value of timeOff with id action.id
		// action.timeOffId - id of the target, used if new value is null
		case Actions.TIMEOFF_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ timeOffReducer: FETCH_ONE_SUCCESS', action);

			if (action.timeOff === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.timeOffId)) {
				st = state.clone();
				st.set(action.timeOff);
				// state.clear();

				updateStorageNoReturn([action.timeOff]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.timeOffs
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.timeOffs
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.timeOffs
			 !partial, !stateEmpty, fetchEmpty	-> D return timeOffs in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge timeOffs in state newer than those in action.timeOffs
		 */
		case Actions.TIMEOFF_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.timeOffs.length === 0;
			const fetched = action.timeOffs.map(timeOff => new TimeOff({...timeOff, persisted: true}));

			if (logging) console.debug(`⮂ timeOffReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} timeOffs with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ timeOffReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ timeOffReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<TimeOff> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: TimeOff[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(timeOff => {
					const current = st.get(timeOff.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && timeOff.status === 'D') {
							// Remove it from state
							toUpdate.push(timeOff);
							st.delete(timeOff);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (timeOff.addedOn > current.addedOn) {
								toUpdate.push(timeOff);
								st.set(timeOff);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (timeOff.status !== 'D') {
							toUpdate.push(timeOff);
							st.set(timeOff);
						}
						else if (logging) console.debug('⮂ timeOffReducer: partialMerge: skipping deleted timeOff not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ timeOffReducer: empty: timeOffs', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ timeOffReducer: fullMerge: timeOffs', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localTimeOff => {
                    // If this timeOff has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localTimeOff.persisted) {
                        const fetchedTimeOff = st.get(localTimeOff.id);
                        if (!fetchedTimeOff || localTimeOff.addedOn > fetchedTimeOff.addedOn) st.set(localTimeOff);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.TIMEOFF_FETCH_ONE_FAILURE:
			console.error('TIMEOFF_FETCH_ONE_FAILURE', action.timeOffId, action.err);

			// No update to storage required
			return state;

		case Actions.TIMEOFF_FETCH_FAILURE:
			console.error('TIMEOFF_FETCH_FAILURE', action.timeOffId, action.err);

			// No update to storage required
			return state;

		case Actions.TIMEOFF_RESTORE: {
		    let timeOff = new TimeOff({...action.timeOff});
            st = state.clone();

            const existing = st.get(timeOff.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (timeOff.addedOn >= existing.addedOn) {
                    st.set(timeOff);
                    return updateStorage(st, [timeOff]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (timeOff.status === 'D') return state;

                st.set(timeOff);
                return updateStorage(st, [timeOff]);
            }
        }
            
		case Actions.TIMEOFF_SAVE: {
		    // console.debug('⮂ TIMEOFF_SAVE reducer', action.timeOff?.id);
		    
			// clone the timeOff
			let timeOff = new TimeOff({...action.timeOff});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new timeOff in the state
			// If this is a create, add to the queue
			const _timeOff = st.get(timeOff.id);

			if (!_timeOff) st.set(timeOff);
			else {
				if (timeOff.addedOn >= _timeOff.addedOn) st.set(timeOff);
			}

			return updateStorage(st, [timeOff]);
		}

		case Actions.TIMEOFF_SAVE_MANY: {
		    if (logging) console.debug('⮂ TIMEOFF_SAVE_MANY reducer', action.timeOffs.map(timeOff => timeOff.id).join(','));
		    
			// clone the timeOffs
			let toProcess = action.timeOffs.map(timeOff => new TimeOff(timeOff));

			st = state.clone();
			// state.clear();

			const updates: TimeOff[] = [];

			toProcess.forEach(timeOff => {
				// Add or update the new timeOff in the state
				// If this is a create, add to the queue
				const existing = st.get(timeOff.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (timeOff.status !== 'D') {
						st.push(timeOff);
						updates.push(timeOff);
					}
					else if (logging) console.debug('⮂ TIMEOFF_SAVE_MANY: skipping deleted timeOff not in state');
				}
				else {
					if (timeOff.addedOn >= existing.addedOn) {
						st.set(timeOff);
						updates.push(timeOff);
					}
					else if (logging) console.debug('⮂ Skipping, existing timeOff had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.TIMEOFF_REMOVE: {
			// clone the timeOff
			const timeOff = new TimeOff({...action.timeOff});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(timeOff);

			return updateStorage(st, [timeOff]);
		}

		case Actions.TIMEOFF_RESTORE_FAILURE:
		case Actions.TIMEOFF_SAVE_FAILURE: {
		    console.warn('TIMEOFF_SAVE_FAILURE reducer', action.timeOff?.id);
		    if (!action.timeOff?.id) {
				console.warn('TIMEOFF_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const timeOff = ensure(TimeOff, action.timeOff);
			if (!timeOff) {
				console.error('Could not find timeOff after call to save');
				return state;
			}

			st = state.clone();

			const _timeOffRef = state.get(timeOff.id);

			if (_timeOffRef && (_timeOffRef.addedOn === timeOff.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _timeOff = new TimeOff({..._timeOffRef});
				_timeOff.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_timeOff.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('timeOff', _timeOff).then(r => {
						if (r.isErr()) console.error(`⮂ timeOffReducer: failed to save timeOff with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ timeOffReducer: saved timeOff with fatalError info');
					}).catch(err => console.error(`⮂ timeOffReducer: failed to save timeOff with fatalError info: ${err.message}`));

				st.set(_timeOff);
			}

			return updateStorage(st);
		}

		case Actions.TIMEOFF_SAVE_MANY_FAILURE: {
		    console.warn('TIMEOFF_SAVE_MANY_FAILURE reducer', action.timeOffs.map(timeOff => timeOff.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.TIMEOFF_RESTORE_SUCCESS:
		case Actions.TIMEOFF_SAVE_SUCCESS: {
			if (!action.timeOff?.id) {
				console.warn('TIMEOFF_SAVE_SUCCESS reducer');
                return state;
			}

			const timeOffRef = state.get(action.timeOff?.id);
			if (!timeOffRef) {
				console.warn('Could not find timeOff after call to save');
                return state;
			}

			st = state.clone();

			if (action.timeOff.addedOn >= timeOffRef.addedOn) {
				st.set(action.timeOff);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const timeOff = new TimeOff({...timeOffRef, persisted: true});
				st.set(timeOff);
			}

			return updateStorage(st, [action.timeOff]);
		}

		case Actions.TIMEOFF_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ TIMEOFF_SAVE_MANY_SUCCESS reducer', action.timeOffs.map(timeOff => timeOff.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const timeOffs: TimeOff[] = [];

			action.timeOffs.forEach(e => {
				const timeOffRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!timeOffRef) console.warn('Could not find timeOff after call to save');
				else if (e.addedOn >= timeOffRef.addedOn) {
				    st.set(e);
				    timeOffs.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const timeOff = new TimeOff({...timeOffRef, persisted: true});
					st.set(timeOff);
					timeOffs.push(timeOff);
				}
			});

			return updateStorage(st, timeOffs);
		}

		case Actions.TIMEOFF_REMOVE_FAILURE: {
		    console.warn('TIMEOFF_REMOVE_FAILURE action', action.timeOff?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.TIMEOFF_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.TIMEOFF_RESTORE_MANY: {
			if (logging) console.debug('⮂ TIMEOFF_RESTORE_MANY reducer', action.timeOffs.map(timeOff => timeOff.id).join(','));

			const toProcess = action.timeOffs.map(timeOff => new TimeOff({...timeOff}));
			st = state.clone();

			const updates: TimeOff[] = [];

			toProcess.forEach(timeOff => {
				const existing = st.get(timeOff.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (timeOff.addedOn >= existing.addedOn) {
						st.set(timeOff);
						updates.push(timeOff);
					}
					else if (logging) console.debug('⮂ TIMEOFF_RESTORE_MANY: skipping older version of timeOff');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (timeOff.status !== 'D') {
						st.set(timeOff);
						updates.push(timeOff);
					}
					else if (logging) console.debug('⮂ TIMEOFF_RESTORE_MANY: skipping deleted timeOff not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.TIMEOFF_REMOVE_MANY: {
			if (logging) console.debug('⮂ TIMEOFF_REMOVE_MANY reducer', action.timeOffs.map(timeOff => timeOff.id).join(','));

			const toProcess = action.timeOffs.map(timeOff => new TimeOff({...timeOff}));
			st = state.clone();

			toProcess.forEach(timeOff => {
				st.delete(timeOff);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default timeOffs;